import { c as _c } from "react/compiler-runtime";
import { CenterAligned, classNames, pageContainerClassName } from "@opensea/ui-kit";
import { createContext, useContext, useEffect, useRef } from "react";

/**
 * Generic multi-step container used across different onboarding experiences.
 *
 * It renders only the current step and its adjacent siblings to keep DOM light
 * and reproduces the animation / transition behaviour of the original flow.
 */

const StepContext = createContext({
  presented: false,
  completed: false,
  index: 0,
  currentIndex: 0,
  totalSteps: 1
});
export function StepContainer(t0) {
  const $ = _c(41);
  let background;
  let className;
  let completed;
  let currentIndex;
  let index;
  let presented;
  let ref;
  let rest;
  let totalSteps;
  if ($[0] !== t0) {
    const {
      presented: t1,
      completed: t2,
      onConfirm: _onConfirm,
      index: t3,
      background: t4,
      currentIndex: t5,
      totalSteps: t6,
      className: t7,
      ref: t8,
      ...t9
    } = t0;
    presented = t1;
    completed = t2;
    index = t3;
    background = t4;
    currentIndex = t5;
    totalSteps = t6;
    className = t7;
    ref = t8;
    rest = t9;
    $[0] = t0;
    $[1] = background;
    $[2] = className;
    $[3] = completed;
    $[4] = currentIndex;
    $[5] = index;
    $[6] = presented;
    $[7] = ref;
    $[8] = rest;
    $[9] = totalSteps;
  } else {
    background = $[1];
    className = $[2];
    completed = $[3];
    currentIndex = $[4];
    index = $[5];
    presented = $[6];
    ref = $[7];
    rest = $[8];
    totalSteps = $[9];
  }
  const containerRef = useRef(null);
  const isFirstStep = index === 0;
  const isLastStep = index === totalSteps - 1;
  const isFirstOrLastStep = isFirstStep || isLastStep;
  const shouldRender = currentIndex <= index + 1 && currentIndex >= index - 1;
  const showScaleAnimation = index === 1 && !completed || index === totalSteps - 2 && completed;
  let t1;
  let t2;
  if ($[10] !== presented) {
    t1 = () => {
      if (presented && containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    };
    t2 = [presented];
    $[10] = presented;
    $[11] = t1;
    $[12] = t2;
  } else {
    t1 = $[11];
    t2 = $[12];
  }
  useEffect(t1, t2);
  if (!shouldRender) {
    return null;
  }
  let t3;
  if ($[13] !== completed || $[14] !== currentIndex || $[15] !== index || $[16] !== presented || $[17] !== totalSteps) {
    t3 = {
      presented,
      completed,
      index,
      currentIndex,
      totalSteps
    };
    $[13] = completed;
    $[14] = currentIndex;
    $[15] = index;
    $[16] = presented;
    $[17] = totalSteps;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const t4 = presented ? "opacity-100" : "pointer-events-none opacity-0 blur-sm";
  const t5 = isFirstOrLastStep ? "fixed" : "absolute md:fixed";
  let t6;
  if ($[19] !== className || $[20] !== completed || $[21] !== isFirstOrLastStep || $[22] !== isLastStep || $[23] !== presented || $[24] !== showScaleAnimation || $[25] !== t4 || $[26] !== t5) {
    let t7;
    if ($[28] !== presented || $[29] !== showScaleAnimation) {
      t7 = showScaleAnimation && classNames("duration-1500", !presented && "scale-75");
      $[28] = presented;
      $[29] = showScaleAnimation;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    t6 = classNames("scrollbar-hidden inset-0 size-full justify-center overflow-x-hidden overflow-y-scroll text-center", "z-10 transition ease-out-quint will-change-transform", "pb-12 lg:pb-0", t4, t5, isFirstOrLastStep && classNames("-z-10 duration-1000", !presented && "scale-150"), t7, !(isFirstOrLastStep || showScaleAnimation) && classNames("z-10 duration-500", !presented && (completed ? "-translate-x-10" : "translate-x-10")), isLastStep && completed && "scale-50 opacity-0", pageContainerClassName, className);
    $[19] = className;
    $[20] = completed;
    $[21] = isFirstOrLastStep;
    $[22] = isLastStep;
    $[23] = presented;
    $[24] = showScaleAnimation;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[31] !== ref) {
    t7 = node => {
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
      containerRef.current = node;
    };
    $[31] = ref;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== rest || $[34] !== t6 || $[35] !== t7) {
    t8 = <CenterAligned className={t6} ref={t7} {...rest} />;
    $[33] = rest;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== background || $[38] !== t3 || $[39] !== t8) {
    t9 = <StepContext.Provider value={t3}>{background}{t8}</StepContext.Provider>;
    $[37] = background;
    $[38] = t3;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
}
export const useStepContext = () => {
  return useContext(StepContext);
};